/**
 * Reads the import format description that `Query.importSpecification` returns.
 *
 * Everything here is a *projection* of the backend artifact, never a second copy of
 * it: payload limits are read from the `jsonSchema` string and examples are selected
 * from the files genesis delivered. Spark deliberately keeps no importer field list
 * of its own, so the panel cannot drift from the build that will run the file.
 *
 * Parsing is total: a schema that is unparseable, truncated or from a future shape
 * yields null/empty rather than throwing, so a backend change can degrade the format
 * panel but can never break the import page around it.
 */

/** The two kinds V1 documents in the UI. Both must exist in the backend schema. */
export type ImportSpecKind = "Person" | "SharedItem";

export interface ImportSpecLimits {
  maxPayloadSize: string;
  maxTopLevelDocuments: number;
}

export interface ImportSpecExample {
  fileName: string;
  description: string;
  yaml: string;
}

type Json = Record<string, unknown>;

function parseJson(raw: string): Json | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Json) : null;
  } catch {
    return null;
  }
}

function asObject(value: unknown): Json | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : null;
}

export function parseImportSpecLimits(jsonSchema: string): ImportSpecLimits | null {
  const limits = asObject(parseJson(jsonSchema)?.["x-genesis-limits"]);
  const maxPayloadSize = limits?.maxPayloadSize;
  const maxTopLevelDocuments = limits?.maxTopLevelDocuments;
  if (typeof maxPayloadSize !== "string" || typeof maxTopLevelDocuments !== "number") return null;
  return { maxPayloadSize, maxTopLevelDocuments };
}

// Example file names are generated as `<kind-slug>-<variant>-v<n>.yaml`
// (person-minimal-v1.yaml). Files without a variant are cross-kind examples
// (person-and-shared-item-v1.yaml) and are offered as extras rather than as the
// canonical minimal/full pair.
const EXAMPLE_FILE = /^(?<slug>.+?)-(?<variant>minimal|full)-v\d+\.ya?ml$/;
const KIND_SLUGS: Record<ImportSpecKind, string[]> = {
  Person: ["person"],
  SharedItem: ["shared", "item"],
};

function slugSegments(fileName: string): string[] {
  return fileName
    .replace(/\.ya?ml$/, "")
    .replace(/-v\d+$/, "")
    .split("-");
}

/**
 * Whether `segments` contains `needle` as a consecutive run of WHOLE segments.
 * Substring matching would be wrong in both directions: "shared-item" must match
 * inside "person-and-shared-item", while "person" must NOT match "salesperson".
 */
function containsSegmentRun(segments: string[], needle: string[]): boolean {
  if (needle.length === 0) return false;
  return segments.some((_, index) => needle.every((part, offset) => segments[index + offset] === part));
}

export function selectExamplesForKind(
  examples: readonly ImportSpecExample[],
  kind: ImportSpecKind,
): { minimal: ImportSpecExample | null; full: ImportSpecExample | null } {
  const needle = KIND_SLUGS[kind];
  const pick = (variant: "minimal" | "full") =>
    examples.find((example) => {
      const groups = EXAMPLE_FILE.exec(example.fileName)?.groups;
      const slug = groups?.slug;
      if (!slug || groups.variant !== variant) return false;
      return containsSegmentRun(slug.split("-"), needle);
    }) ?? null;
  return { minimal: pick("minimal"), full: pick("full") };
}

export interface ImportGuideSection {
  title: string;
  body: string;
  /** GitHub-style slug of the title; genesis' `documentationAnchor` links point at it. */
  anchor: string;
  /**
   * Slugs of the `###` sub-chapters inside the body. genesis' guidance also
   * links per document kind (e.g. `#expertise` inside "Fields per kind"), and
   * the owning chapter has to open for those too.
   */
  subAnchors: string[];
}

function sectionAnchor(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .trim()
    .replace(/\s+/g, "-");
}

export interface ImportGuide {
  intro: string;
  sections: ImportGuideSection[];
}

/**
 * Splits the generated guide at its `## ` chapters so the panel can render each
 * one as a collapsible section instead of one nested scrollbox.
 *
 * Fence-aware: a line starting with `## ` inside a ``` block is YAML content,
 * not a chapter. The single leading `# ` document title is dropped - the panel
 * header and the limits line already say what this is. Total like everything
 * else in this file: markdown without any `## ` chapter comes back as intro
 * only, which the panel renders whole, so a generator change can degrade the
 * sectioning but never lose text.
 */
export function splitImportGuide(markdown: string): ImportGuide {
  const intro: string[] = [];
  const chapters: { title: string; lines: string[] }[] = [];
  let current = intro;
  let inFence = false;
  let titleDropped = false;

  for (const line of markdown.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    if (!inFence) {
      if (!titleDropped && chapters.length === 0 && /^# (?!#)/.test(line)) {
        titleDropped = true;
        continue;
      }
      const title = /^## (?!#)(.+?)\s*$/.exec(line)?.[1];
      if (title) {
        current = [];
        chapters.push({ title, lines: current });
        continue;
      }
    }
    current.push(line);
  }

  return {
    intro: intro.join("\n").trim(),
    sections: chapters.map(({ title, lines }) => ({
      title,
      body: lines.join("\n").trim(),
      anchor: sectionAnchor(title),
      subAnchors: lines
        .map((line) => /^### (?!#)(.+?)\s*$/.exec(line)?.[1])
        .filter((subTitle): subTitle is string => Boolean(subTitle))
        .map(sectionAnchor),
    })),
  };
}

/** Backend examples that mention this kind but are not its minimal/full pair. */
export function additionalExamplesForKind(
  examples: readonly ImportSpecExample[],
  kind: ImportSpecKind,
): ImportSpecExample[] {
  const needle = KIND_SLUGS[kind];
  return examples.filter(
    (example) =>
      !EXAMPLE_FILE.test(example.fileName) && containsSegmentRun(slugSegments(example.fileName), needle),
  );
}
